/**
 * Converts enum values to friendly display labels
 */

export function formatEnumLabel(value: string): string {
  // Handle common patterns
  return value
    // Add space before capital letters (camelCase to Title Case)
    .replace(/([A-Z])/g, ' $1')
    // Trim leading space
    .trim()
    // Capitalize first letter of each word
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// Specific formatters for audit enums
export function formatAuditStatus(status: string): string {
  const labels: Record<string, string> = {
    'Assigned': 'Assigned',
    'InProgress': 'In Progress',
    'UnderReview': 'Under Review',
    'OnHold': 'On Hold',
    'Completed': 'Completed',
    'Cancelled': 'Cancelled',
  };
  return labels[status] || formatEnumLabel(status);
}

export function formatAuditPriority(priority: string): string {
  const labels: Record<string, string> = {
    'Critical': 'Critical',
    'High': 'High',
    'Medium': 'Medium',
    'Low': 'Low',
  };
  return labels[priority] || formatEnumLabel(priority);
}

export function formatAuditType(type: string): string {
  const labels: Record<string, string> = {
    'Physical': 'Physical',
    'Financial': 'Financial',
    'Compliance': 'Compliance',
    'Condition': 'Condition',
    'Location': 'Location',
  };
  return labels[type] || formatEnumLabel(type);
}

export function formatExceptionStatus(status: string): string {
  const labels: Record<string, string> = {
    'Open': 'Open',
    'InProgress': 'In Progress',
    'Resolved': 'Resolved',
    'Closed': 'Closed',
  };
  return labels[status] || formatEnumLabel(status);
}

export function formatExceptionSeverity(severity: string): string {
  const labels: Record<string, string> = {
    'Critical': 'Critical',
    'High': 'High',
    'Medium': 'Medium',
    'Low': 'Low',
  };
  return labels[severity] || formatEnumLabel(severity);
}

export function formatExceptionType(type: string): string {
  const labels: Record<string, string> = {
    'Missing': 'Missing',
    'Damaged': 'Damaged',
    'LocationMismatch': 'Location Mismatch',
    'ValueDiscrepancy': 'Value Discrepancy',
    'DocumentationIssue': 'Documentation Issue',
    'ComplianceViolation': 'Compliance Violation',
  };
  return labels[type] || formatEnumLabel(type);
}

// Formatters for audit log actions
export function formatActionType(action: string | undefined | null): string {
  if (!action) return '';
  
  const labels: Record<string, string> = {
    'Create': 'Created',
    'Update': 'Updated',
    'Delete': 'Deleted',
    'StatusChange': 'Status Changed',
    'AssignmentChange': 'Assignment Changed',
    'CommentAdded': 'Comment Added',
    'AttachmentAdded': 'Attachment Added',
    'AttachmentRemoved': 'Attachment Removed',
    'ExceptionCreated': 'Exception Created',
    'ExceptionResolved': 'Exception Resolved',
  };
  return labels[action] || formatEnumLabel(action);
}

export function formatFieldName(field: string): string {
  const labels: Record<string, string> = {
    'status': 'Status',
    'priority': 'Priority',
    'assignedTo': 'Assigned To',
    'dueDate': 'Due Date',
    'completionDate': 'Completion Date',
    'notes': 'Notes',
    'findings': 'Findings',
    'recommendations': 'Recommendations',
    'location': 'Location',
    'condition': 'Condition',
    'value': 'Value',
    'description': 'Description',
    'type': 'Type',
  };
  return labels[field] || formatEnumLabel(field);
}

/**
 * Formats technical API error messages into user-friendly text.
 * Examples:
 * - "DetailLines().NominalUpdate.Debit.Code=1000" -> "Nominal Update Debit Code: 1000"
 * - "Nominal account currency mismatch: DetailLines().NominalUpdate.Debit.Code=1000" 
 *   -> "Nominal account currency mismatch: Nominal Update Debit Code: 1000"
 */
export function formatApiErrorMessage(message: string): string {
  if (!message) return '';
  
  // Split by colon to preserve the main error message prefix
  const colonIndex = message.indexOf(':');
  let prefix = '';
  let technicalPart = message;
  
  if (colonIndex > 0) {
    prefix = message.substring(0, colonIndex + 1) + ' ';
    technicalPart = message.substring(colonIndex + 1).trim();
  }
  
  // Format the technical part
  const formatted = formatTechnicalPath(technicalPart);
  
  return prefix + formatted;
}

/**
 * Formats a technical path like "DetailLines().NominalUpdate.Debit.Code=1000"
 * into readable text like "Nominal Update Debit Code: 1000"
 */
function formatTechnicalPath(path: string): string {
  if (!path) return '';
  
  // Check if there's an equals sign (key=value pattern)
  const equalsIndex = path.lastIndexOf('=');
  let value = '';
  let pathPart = path;
  
  if (equalsIndex > 0) {
    value = path.substring(equalsIndex + 1).trim();
    pathPart = path.substring(0, equalsIndex);
  }
  
  // Remove parentheses (e.g., "DetailLines()" -> "DetailLines")
  pathPart = pathPart.replace(/\(\)/g, '');
  
  // Split by dots and get meaningful parts (skip common prefixes like "DetailLines")
  const parts = pathPart.split('.').filter(part => {
    const lowerPart = part.toLowerCase();
    return part && lowerPart !== 'detaillines' && lowerPart !== 'detail' && lowerPart !== 'lines';
  });
  
  // Convert each part from camelCase/PascalCase to readable text
  const readableParts = parts.map(part => {
    return part
      // Add space before capital letters
      .replace(/([A-Z])/g, ' $1')
      .trim()
      // Capitalize first letter
      .replace(/^\w/, c => c.toUpperCase());
  });
  
  // Join parts and add value if present
  const readablePath = readableParts.join(' ');
  
  if (value) {
    return `${readablePath}: ${value}`;
  }
  
  return readablePath || path;
}
